import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, Button, Slider, Chip,
  Avatar, Card, Grid, CircularProgress, Alert,
  InputAdornment, Divider,
} from '@mui/material';
import {
  PhotoCamera, Save, ArrowBack, LocationOn,
  AttachMoney, WorkOutline, Gavel, Description,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { updateProfile } from '../../store/slices/authSlice';
import { axelionColors } from '../../theme/axelionTheme';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const SPECIALIZATIONS = [
  'Гражданское право', 'Семейное право', 'Трудовое право',
  'Уголовное право', 'Коммерческое право', 'Налоговое право',
  'Административное право', 'Земельное право', 'Корпоративное право',
  'Интеллектуальная собственность', 'Жилищное право', 'Страховое право',
];

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: axelionColors.bgLight,
    borderRadius: '8px',
    '& fieldset': { borderColor: axelionColors.borderLight },
    '&:hover fieldset': { borderColor: axelionColors.gold },
    '&.Mui-focused fieldset': { borderColor: axelionColors.gold, borderWidth: 1 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: axelionColors.gold },
};

const LawyerProfileEditPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    description: '',
    experience: 0,
    price: 200000,
    location: 'Ташкент',
    specialization: '',
    schedule: {},
    avatarFile: null,
    avatarPreview: null,
  });

  // Load current profile
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/lawyer/profile');
        const p = res.data.profile || {};
        setForm({
          description: p.description || '',
          experience: p.experience || 0,
          price: p.price || 200000,
          location: p.location || 'Ташкент',
          specialization: p.specialization || '',
          schedule: p.schedule || {},
          avatarFile: null,
          avatarPreview: res.data.user?.avatar || null,
        });
      } catch {
        setError('Не удалось загрузить профиль');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDay = (key) => {
    const current = form.schedule[key];
    handleChange('schedule', {
      ...form.schedule,
      [key]: current ? null : { start: '09:00', end: '18:00' },
    });
  };

  const updateTime = (key, field, value) => {
    handleChange('schedule', {
      ...form.schedule,
      [key]: { ...form.schedule[key], [field]: value },
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleChange('avatarFile', file);
      handleChange('avatarPreview', URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!form.description || form.description.length < 50) {
      toast.error('Описание должно быть не менее 50 символов');
      return;
    }
    if (!form.specialization) {
      toast.error('Выберите специализацию');
      return;
    }
    if (form.price < 50000) {
      toast.error('Минимальная цена: 50 000 сум');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('description', form.description);
      formData.append('experience', String(form.experience));
      formData.append('specialization', form.specialization);
      formData.append('price', String(form.price));
      formData.append('location', form.location);
      formData.append('schedule', JSON.stringify(form.schedule));
      if (form.avatarFile) formData.append('avatar', form.avatarFile);

      const res = await api.put('/lawyer/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Update avatar in Redux store if it changed
      if (res.data.user?.avatar) {
        dispatch(updateProfile({ avatar: res.data.user.avatar }));
      }

      toast.success('Профиль сохранён!');
      navigate('/lawyer/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: axelionColors.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: axelionColors.bgCream, pb: 6 }}>
      {/* Header */}
      <Box sx={{ bgcolor: axelionColors.bgLight, borderBottom: `1px solid ${axelionColors.borderLight}`, py: 3, px: 2 }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/lawyer/dashboard')}
            sx={{ color: axelionColors.textMuted, textTransform: 'none', '&:hover': { color: axelionColors.gold } }}
          >
            Назад
          </Button>
          <Typography sx={{ fontWeight: 300, fontSize: '1.5rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: axelionColors.textDark }}>
            Редактировать профиль
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, mt: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>{error}</Alert>}

        {/* Avatar */}
        <Card sx={{ p: 3, mb: 3, border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px', bgcolor: axelionColors.bgLight }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: axelionColors.textDark, mb: 2 }}>
            Фото профиля
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={form.avatarPreview}
              sx={{ width: 80, height: 80, bgcolor: axelionColors.bgBeige, color: axelionColors.gold, fontSize: '2rem', border: `2px solid ${axelionColors.gold}` }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
            <Button component="label" variant="outlined" startIcon={<PhotoCamera />}
              sx={{ borderColor: axelionColors.gold, color: axelionColors.gold, borderRadius: '8px', textTransform: 'none', '&:hover': { bgcolor: axelionColors.accentLight } }}
            >
              Загрузить фото
              <input hidden type="file" accept="image/*" onChange={handleAvatarChange} />
            </Button>
          </Box>
        </Card>

        {/* Basic info */}
        <Card sx={{ p: 3, mb: 3, border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px', bgcolor: axelionColors.bgLight }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: axelionColors.textDark, mb: 3 }}>
            Основная информация
          </Typography>

          <TextField
            fullWidth multiline rows={4}
            label="О себе"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Расскажите о вашем опыте, образовании, специализации..."
            helperText={`${form.description.length}/500 символов (минимум 50)`}
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 3, ...inputStyles }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Description sx={{ color: axelionColors.textMuted }} /></InputAdornment> }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '0.8rem', color: axelionColors.textMuted, mb: 1 }}>
              Опыт работы: <b>{form.experience === 0 ? 'Без опыта' : `${form.experience} лет`}</b>
            </Typography>
            <Slider
              value={form.experience}
              onChange={(_, v) => handleChange('experience', v)}
              min={0} max={40} step={1}
              marks={[{ value: 0, label: '0' }, { value: 10, label: '10' }, { value: 20, label: '20' }, { value: 40, label: '40+' }]}
              sx={{ color: axelionColors.gold }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Город"
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                sx={{ ...inputStyles }}
                InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ color: axelionColors.textMuted }} /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Цена консультации (сум)"
                type="number"
                value={form.price}
                onChange={(e) => handleChange('price', Number(e.target.value))}
                inputProps={{ min: 50000, step: 10000 }}
                helperText="Минимум 50 000 сум"
                sx={{ ...inputStyles }}
                InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney sx={{ color: axelionColors.textMuted }} /></InputAdornment> }}
              />
            </Grid>
          </Grid>
        </Card>

        {/* Specialization */}
        <Card sx={{ p: 3, mb: 3, border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px', bgcolor: axelionColors.bgLight }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: axelionColors.textDark, mb: 2 }}>
            Специализация
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SPECIALIZATIONS.map((spec) => {
              const selected = form.specialization === spec;
              return (
                <Chip
                  key={spec}
                  label={spec}
                  icon={<Gavel sx={{ fontSize: 14 }} />}
                  onClick={() => handleChange('specialization', selected ? '' : spec)}
                  sx={{
                    bgcolor: selected ? axelionColors.gold : 'transparent',
                    color: selected ? '#fff' : axelionColors.textSecondary,
                    border: `1px solid ${selected ? axelionColors.gold : axelionColors.borderLight}`,
                    '&:hover': { bgcolor: selected ? axelionColors.goldDark : axelionColors.bgWarm },
                  }}
                />
              );
            })}
          </Box>
        </Card>

        {/* Schedule */}
        <Card sx={{ p: 3, mb: 3, border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px', bgcolor: axelionColors.bgLight }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: axelionColors.textDark, mb: 2 }}>
            Расписание
          </Typography>
          {DAY_KEYS.map((key, idx) => {
            const active = !!form.schedule[key];
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Chip
                  label={DAYS[idx]}
                  onClick={() => toggleDay(key)}
                  size="small"
                  sx={{
                    width: 48, cursor: 'pointer',
                    bgcolor: active ? axelionColors.gold : 'transparent',
                    color: active ? '#fff' : axelionColors.textSecondary,
                    border: `1px solid ${active ? axelionColors.gold : axelionColors.borderLight}`,
                    '&:hover': { bgcolor: active ? axelionColors.goldDark : axelionColors.bgWarm },
                  }}
                />
                {active && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField type="time" size="small" value={form.schedule[key].start}
                      onChange={(e) => updateTime(key, 'start', e.target.value)}
                      sx={{ width: 120 }} />
                    <Typography variant="caption" color="text.secondary">—</Typography>
                    <TextField type="time" size="small" value={form.schedule[key].end}
                      onChange={(e) => updateTime(key, 'end', e.target.value)}
                      sx={{ width: 120 }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Card>

        {/* Save button */}
        <Button
          fullWidth variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Save />}
          sx={{
            bgcolor: axelionColors.gold,
            color: '#fff',
            py: 1.75,
            borderRadius: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: 500,
            fontSize: '0.9rem',
            boxShadow: 'none',
            '&:hover': { bgcolor: axelionColors.goldDark, boxShadow: 'none' },
          }}
        >
          {saving ? 'Сохраняем...' : 'Сохранить профиль'}
        </Button>
      </Box>
    </Box>
  );
};

export default LawyerProfileEditPage;
